import React from 'react';

import { hexAlpha, objectValues } from '@nfctron/nexus-core';
import { type ExtendedComponent, UiBadge, cn, useContextRequired, useFormat } from '@nfctron/nexus-react';
import { NfcChipIcon } from '@nfctron/nexus-react-svg';

import { useMantineTheme } from '@mantine/core';
import { UserIcon } from 'lucide-react';

import { TicketCardDivider } from '@root/cart/cart/_resources/components/Ticket/TicketCardDivider';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { ITEM_TYPE } from '@root/lib/constants/itemType';
import { useTranslate } from '@root/lib/hooks/useTranslation';

import { getEventTypeSensitiveComponentDisplayName } from '../../../../_resources/helpers/common';
import { type CartStoreCartedTicketsSlice, type CartStoreTicketTypesSlice, useCartStore } from '../../stores/cartStore';
import { EventCartPageContext } from '../CartContext';

const bottomBadgesMaxShown = 3;

/**
 * Event/Cart/ImportedTicket card component
 * TODO: refactor
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ImportedTicketCard: React.FunctionComponent<
	ExtendedComponent<{
		ticketType: CartStoreTicketTypesSlice.TicketTypeConfig;
		importedTicket: CartStoreCartedTicketsSlice.ImportedTicket;
	}>
> = ({ importedTicket, ticketType, children, className, ...props }) => {
	const format = useFormat();
	const { event } = useContextRequired(EventCartPageContext);
	const theme = useMantineTheme();
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ImportedTicketCard.displayName);
	const { t: typeSensitiveT } = useTranslate(
		APP_I18N_NAMESPACE.MAIN,
		getEventTypeSensitiveComponentDisplayName(ImportedTicketCard.displayName, event.eventType.eventTypeId),
	);

	/** get ticket type properties using cartStore */
	const hasChips = useCartStore((state) => state.hasTicketTypeChips({ ticketType }));

	/** get ticket type content entry items */
	const contentEntries = ticketType.contentProductItems.reduce(
		(acc, item) => acc + (item.itemTypeId === ITEM_TYPE.EVENT_ENTRY ? item.quantity : 0),
		0,
	);

	/** bottom side badges with features and info content */
	const badgeClassNames = cn('px-2 h-6 uppercase text-sm font-bold');
	const bottomBadges = React.useMemo(() => {
		/** TODO: add UiDrawers with content for badges */
		const badgesMap = {
			/** contains chips */
			chips: hasChips && (
				<UiBadge
					key="badge-chips"
					color="lucky-point"
					variant="subtle"
					rounded="full"
					className={badgeClassNames}
					leftSection={<NfcChipIcon className="text-sm" />}
					tooltip={typeSensitiveT('chipsBadge.tooltip', { count: ticketType.chips.length, formattedCount: format.number(ticketType.chips.length) })}
				>
					{t('chipsBadge.text', { count: ticketType.chips.length, formattedCount: format.number(ticketType.chips.length) })}
				</UiBadge>
			),
			/** contains entries */
			entries: contentEntries > 0 && (
				<UiBadge
					key="badge-entries"
					color="lucky-point"
					variant="subtle"
					rounded="full"
					className={badgeClassNames}
					leftSection={<UserIcon className="text-sm" />}
					tooltip={typeSensitiveT('entriesBadge.tooltip', { count: contentEntries, formattedCount: format.number(contentEntries) })}
				>
					{t('entriesBadge.text', { count: contentEntries, formattedCount: format.number(contentEntries) })}
				</UiBadge>
			),
		};

		return objectValues(badgesMap).filter(Boolean) as React.JSX.Element[];
	}, [badgeClassNames, contentEntries, format, hasChips, t, ticketType.chips.length, typeSensitiveT]);

	return (
		<div className={cn('flex flex-col gap-3 rounded-xl border border-gray-200 bg-white py-4', className)} key={ticketType.ticketTypeId} {...props}>
			<div className="flex max-w-full gap-3 px-4">
				{/* left col */}
				<div className="flex grow flex-col gap-4">
					<div>
						{/* ticket type name */}
						<span className="text-lucky-point-500 block text-lg font-bold">{ticketType.namePub}</span>
						{/* ticket holder name */}
						<span className="block text-base">{importedTicket.holderName}</span>
					</div>
					{/* bottom badges */}
					<div className="flex flex-wrap items-center gap-2">
						{/* show truncated list of badges */}
						{bottomBadges.slice(0, bottomBadgesMaxShown)}
						{/* display badge with omitted badge labels */}
						{bottomBadges.length > bottomBadgesMaxShown && (
							<UiBadge
								color="lucky-point"
								variant="subtle"
								rounded="full"
								className={badgeClassNames}
								tooltip={
									<ul className="list-disc pl-3">
										{bottomBadges.slice(bottomBadgesMaxShown).map((badge) => (
											<li key={badge.key}>{badge.props.tooltip}</li>
										))}
									</ul>
								}
							>
								+&nbsp;{format.number(bottomBadges.length - bottomBadgesMaxShown)}
							</UiBadge>
						)}
					</div>
				</div>

				{/* right col */}
				{ticketType.price > 0 && (
					<div className="flex min-w-1/4 shrink-0 flex-col gap-3">
						{/* ticket type price */}
						<div className="flex flex-col items-end">
							<span className="block text-right text-xl font-bold text-black">
								{format.price(ticketType.price, event.currency.baseCurrency.currencyId)}
							</span>
						</div>
					</div>
				)}
			</div>

			{/* divider */}
			<TicketCardDivider bgColor={hexAlpha(theme.colors.gray[0])} />

			{/* controls */}
			<footer className="px-4">{children}</footer>
		</div>
	);
};

ImportedTicketCard.displayName = 'Event/Cart/ImportedTicketCard';
